import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '@HoudiniUI/Collapsible';
import { css } from 'aphrodite';
import * as React from 'react';
import { useMetaConnectionQuery } from '../../../queries/Admin/Meta';
import { useNoteByIdQuery } from '../../../queries/Note/useNoteByIdQuery';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { Pill } from '../../../web/components-v2/Pill';
import { formatUtcTimestamp } from '../../containers/adsMeta/utils';
import { DetailRow } from './DetailRow';
import { inboxStyles as s } from './styles/inboxStyles';
import {
	type AdsAccountInfo,
	type AdsChannelInfo,
	type AdsDetailsInfo,
	formatNoteByline,
	getAdsDetails,
} from './supportTicketPresentation';

interface TicketAdsPanelProps {
	accountId: string;
	account: AdsAccountInfo | undefined;
}

const LivePill: React.FC<{ isLive: boolean }> = ({ isLive }) => (
	<Pill variant='tag' color={isLive ? 'green' : 'red'} size='small' label={isLive ? 'Live' : 'Not live'} />
);

/**
 * Latest meeting note for one channel, read-only. Notes are composed on the account details page —
 * the drawer shows the newest one so an agent can see what the ads team last discussed.
 */
const AdsNote: React.FC<{ noteId: string }> = ({ noteId }) => {
	const { data: note } = useNoteByIdQuery(noteId);
	const body = note?.plainTextContent?.trim() || note?.preview?.trim();
	if (!note || !body) return null;

	return (
		<div className={css(s.adsNote)}>
			<div className={css(s.adsNoteByline)}>{formatNoteByline(note)}</div>
			<div className={css(s.adsNoteBody)}>{body}</div>
		</div>
	);
};

/**
 * Meta lead-ingestion and metrics-sync health. This is the channel's most actionable signal on a
 * support ticket — a sync error here is usually the reason a customer says their ads stopped
 * working — so it renders ahead of anything cosmetic.
 */
const MetaHealth: React.FC<{ accountId: string }> = ({ accountId }) => {
	const { data } = useMetaConnectionQuery({ levitateAccountId: accountId });
	if (!data) return null;

	const isAdsTeamManaging = data.managedAdAccountIds.length > 0;
	const leadCount = data.lastPollLeadCount;
	const hasAnySignal =
		data.hasCustomerToken ||
		isAdsTeamManaging ||
		!!data.lastPollTimestamp ||
		!!data.lastPollError ||
		!!data.lastMetricsSyncAt ||
		!!data.lastMetricsSyncError;
	if (!hasAnySignal) return null;

	return (
		<>
			<div className={css(s.detailSectionTitle, s.adsBlockLabel)}>Connection health</div>
			<div className={css(s.adsRows)}>
				<div className={css(s.adsHealthPills)}>
					<Pill
						variant='tag'
						size='small'
						color={data.hasCustomerToken ? 'green' : 'gray'}
						label={data.hasCustomerToken ? 'OAuth connected' : 'No customer OAuth'}
					/>
					<Pill
						variant='tag'
						size='small'
						color={isAdsTeamManaging ? 'green' : 'gray'}
						label={isAdsTeamManaging ? 'Ads team managing' : 'Not managed'}
					/>
				</div>
				{data.lastPollTimestamp && (
					<DetailRow label='Last poll'>
						{formatUtcTimestamp(data.lastPollTimestamp)}
						{typeof leadCount === 'number' ? ` · ${leadCount} ${leadCount === 1 ? 'lead' : 'leads'}` : null}
					</DetailRow>
				)}
				{data.lastMetricsSyncAt && (
					<DetailRow label='Last sync'>{formatUtcTimestamp(data.lastMetricsSyncAt)}</DetailRow>
				)}
				{data.lastPollError && <AdsHealthError label='Poll error'>{data.lastPollError}</AdsHealthError>}
				{data.lastMetricsSyncError && (
					<AdsHealthError label={`Sync error · ${formatUtcTimestamp(data.lastMetricsSyncError.timestamp)}`}>
						{data.lastMetricsSyncError.message}
					</AdsHealthError>
				)}
			</div>
		</>
	);
};

const AdsHealthError: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className={css(s.adsHealthError)}>
		<div className={css(s.adsHealthErrorLabel)}>{label}</div>
		<div className={css(s.adsHealthErrorMessage)}>{children}</div>
	</div>
);

const AdsCard: React.FC<{ title: string; chips: React.ReactNode; children: React.ReactNode }> = ({
	title,
	chips,
	children,
}) => (
	<div className={css(s.adsCard)}>
		<div className={css(s.adsCardHeader)}>
			<span className={css(s.adsCardTitle)}>{title}</span>
			{chips}
		</div>
		<div className={css(s.adsRows)}>{children}</div>
	</div>
);

const SharedAdsFields: React.FC<{ ads: AdsDetailsInfo; liveDate: string; liveDateLabel: string }> = ({
	ads,
	liveDate,
	liveDateLabel,
}) => (
	<>
		<DetailRow label='Specialist'>{ads.specialist}</DetailRow>
		<DetailRow label='Kickoff'>{ads.kickoffDate}</DetailRow>
		<DetailRow label={liveDateLabel}>{liveDate}</DetailRow>
		<DetailRow label='Last contact'>{ads.lastContact}</DetailRow>
		<DetailRow label='Last meeting'>{ads.lastMeeting}</DetailRow>
		<DetailRow label='Next meeting'>{ads.nextMeeting}</DetailRow>
	</>
);

const ChannelExtras: React.FC<{ accountId: string; channel: AdsChannelInfo }> = ({ accountId, channel }) => (
	<>
		{channel.noteId && <AdsNote noteId={channel.noteId} />}
		{channel.key === 'facebook' && <MetaHealth accountId={accountId} />}
	</>
);

const summarizeChannels = (ads: AdsDetailsInfo): string => {
	if (ads.channels.length === 0) return 'No channels';
	if (ads.channels.length === 1) return ads.channels[0].label;
	return `${ads.channels.length} channels`;
};

/**
 * Advertising block of the ticket details drawer: the read-only half of the account details Ads tab,
 * so an agent working an ads ticket does not have to open the account in another tab.
 *
 * Collapsed by default. Most tickets are not ads tickets, and the panel below keeps the note and Meta
 * connection queries from firing until an agent actually opens it — the collapsed header still
 * answers the one question worth asking on every ticket, which is whether the account is live.
 *
 * A single-channel account folds the account-wide fields into the one channel card, matching the
 * account details page — with only one channel the account-wide dates and the channel's own are the
 * same values, and two cards would repeat them.
 */
export const TicketAdsPanel: React.FC<TicketAdsPanelProps> = ({ accountId, account }) => {
	const [isOpen, setIsOpen] = React.useState(false);
	// Once opened the panel stays mounted: useMetaConnectionQuery is refetchOnMount:'always', so letting
	// the panel unmount on collapse re-fires the request — and its error toast — on every re-expand.
	const [hasOpened, setHasOpened] = React.useState(false);
	const ads = getAdsDetails(account);
	if (!ads) return null;

	const isSingleChannel = ads.channels.length === 1;
	const isLive = isSingleChannel ? ads.channels[0].isLive || ads.isLive : ads.isLive;
	return (
		<Collapsible
			open={isOpen}
			onOpenChange={open => {
				setIsOpen(open);
				if (open) setHasOpened(true);
			}}
		>
			{/* The pill stays outside the trigger: Pill's root is a div, and a button may only contain
			    phrasing content. */}
			<div className={css(s.adsToggleRow)}>
				<CollapsibleTrigger className={css(s.adsToggle)}>
					<ChevronDown
						width={12}
						height={12}
						strokeWidth={3}
						className={css(s.adsToggleChevron, !isOpen && s.adsToggleChevronCollapsed)}
					/>
					<span className={css(s.adsToggleTitle)}>Advertising</span>
					<span className={css(s.adsToggleSummary)}>{summarizeChannels(ads)}</span>
				</CollapsibleTrigger>
				<LivePill isLive={isLive} />
			</div>
			<CollapsiblePanel keepMounted={hasOpened}>
				<div className={css(s.adsCards)}>
					{!isSingleChannel && (
						<AdsCard
							title='Advertising status'
							chips={<Pill variant='tag' color='purple' size='small' label={ads.stage} />}
						>
							<SharedAdsFields ads={ads} liveDate={ads.liveDate} liveDateLabel='Fully live' />
							{ads.channels.length === 0 && <DetailRow label='Channels'>None enabled</DetailRow>}
							{ads.legacyNoteId && <AdsNote noteId={ads.legacyNoteId} />}
						</AdsCard>
					)}

					{ads.channels.map(channel => (
						<AdsCard
							key={channel.key}
							title={channel.label}
							chips={
								<>
									{isSingleChannel && <Pill variant='tag' color='purple' size='small' label={ads.stage} />}
									{!isSingleChannel && <LivePill isLive={channel.isLive} />}
								</>
							}
						>
							{isSingleChannel ? (
								<SharedAdsFields ads={ads} liveDate={channel.liveDate} liveDateLabel='Live date' />
							) : (
								<>
									<DetailRow label='Live date'>{channel.liveDate}</DetailRow>
									<DetailRow label='Last meeting'>{channel.lastMeeting}</DetailRow>
									<DetailRow label='Next meeting'>{channel.nextMeeting}</DetailRow>
								</>
							)}
							<ChannelExtras accountId={accountId} channel={channel} />
						</AdsCard>
					))}
				</div>
			</CollapsiblePanel>
		</Collapsible>
	);
};
